import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { selectSecondaryAddresses } from 'reducers/selectors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useUserAddress } from 'hooks';
import { OnboardingFlow } from 'utils/types/navigation';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { needsPreviousAddress } from '../utils';
import { setPendingPreviousAddress } from '../reducers/creditScoreOnboarding';
import CreditScoreAddressSection from '../components/CreditScoreAddressSection';
const strings = {
    title: 'Confirm your address and the date you moved here',
    titleCollapsed: 'Your address',
    buttonNext: 'Continue',
};
const CreditScoreReviewAddressScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const secondaryAddresses = useAppSelector(selectSecondaryAddresses);
    const { primaryAddress } = useUserAddress();
    const pendingPrimaryAddress = useAppSelector((state) => state.creditScoreOnboarding.pendingPrimaryAddress);
    const pendingMovingDate = useAppSelector((state) => state.creditScoreOnboarding.pendingMovingDate);
    const address = pendingPrimaryAddress || primaryAddress || undefined;
    const movingDate = pendingMovingDate || address?.startDate;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const isFormComplete = !!address && !!movingDate;
    const onPressContinue = useCallback(() => {
        if (!address || !movingDate)
            return;
        const updatedAddress = { ...address, startDate: movingDate };
        if (!needsPreviousAddress(updatedAddress)) {
            dispatch(setPendingPreviousAddress(undefined));
            navigation.navigate('CreditScoreRegistration');
            return;
        }
        // Needs previous address — navigate to add or choose one
        if (secondaryAddresses.length > 0) {
            navigation.navigate('ChoosePreviousAddressScreen', {
                flow: OnboardingFlow.CreditScore,
            });
        }
        else {
            navigation.navigate('AddressPostCodeScreen', {
                isAddingSecondaryAddress: true,
                flow: OnboardingFlow.CreditScore,
            });
        }
    }, [address, movingDate, secondaryAddresses.length]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const scrollViewStyle = useMarginBottom('paddingBottom', HEIGHT, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={scrollViewStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={strings.title}/>
            <CardView noMarginTop>
              <CreditScoreAddressSection />
            </CardView>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button brand disabled={!isFormComplete} onPress={onPressContinue} title={strings.buttonNext}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: strings.titleCollapsed,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditScoreReviewAddressScreen;
